import { Badge, Card, ProgressBar, Text } from "@/design-system";
import { t } from "@/context/language";
import {
  AnimatePresence,
  motion,
  useInView,
  useReducedMotion,
} from "framer-motion";
import { CheckCheck, LoaderCircle, MessageSquareShare } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { MessagesAvatarStack } from "./messagesAvatarStack";

// Disparo ilustrativo: números fictícios — a legenda diz isso a quem escuta.
const TOTAL = 1007;
// Quadro parado (reduced-motion e primeira pintura): o mesmo da arte original.
const START_SENT = 25;
const TICK_MS = 160;
// Lotes de tamanho variado, para o contador não subir com cara de cronômetro.
const BATCHES = [7, 12, 9, 14, 8, 11, 6, 13];
const DONE_HOLD_MS = 2600;

const formatCount = (value: number) => value.toLocaleString("pt-BR");

export function SolutionMessagesCard() {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { margin: "-80px" });
  const reduceMotion = useReducedMotion();

  const [sent, setSent] = useState(START_SENT);
  const done = sent >= TOTAL;
  const running = inView && !reduceMotion;

  useEffect(() => {
    if (!running) return;
    if (done) {
      // Segura o "Concluído" na tela e recomeça um disparo novo.
      const id = window.setTimeout(() => setSent(0), DONE_HOLD_MS);
      return () => window.clearTimeout(id);
    }
    let step = 0;
    const id = window.setInterval(() => {
      const batch = BATCHES[step++ % BATCHES.length];
      setSent(prev => Math.min(prev + batch, TOTAL));
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [running, done]);

  const status = done
    ? {
        key: "done",
        tone: "success" as const,
        label: t.solutions.messagesCard.done,
      }
    : {
        key: "processing",
        tone: "warning" as const,
        label: t.solutions.messagesCard.processing,
      };

  return (
    <figure ref={ref} className="flex w-full max-w-md flex-col gap-3">
      <figcaption className="sr-only">
        {t.solutions.messagesCard.caption}
      </figcaption>

      <Card
        className="rounded-ds-xl bg-ds-paper-marketing flex flex-col gap-4 border-transparent sm:p-5"
        aria-hidden="true"
      >
        <div className="flex items-start justify-between gap-4">
          <Text
            as="span"
            weight="semibold"
            className="text-ds-h3 leading-tight"
          >
            {t.solutions.messagesCard.title}
          </Text>
          <span className="rounded-ds-md bg-ds-secondary-200 text-ds-ink-900 flex size-12 shrink-0 items-center justify-center">
            <MessageSquareShare className="size-6" aria-hidden="true" />
          </span>
        </div>

        <div className="rounded-ds-lg border-ds-border flex flex-col gap-3 border p-3 sm:p-4">
          <div className="flex flex-col">
            <Text
              as="span"
              className="font-ds-display text-ds-h2 leading-none font-bold"
            >
              {formatCount(sent)}
            </Text>
            <Text as="span" tone="muted">
              {t.solutions.messagesCard.sent}
            </Text>
          </div>
          <ProgressBar
            value={sent}
            max={TOTAL}
            label={t.solutions.messagesCard.progress}
            hideLabel
            tone={done ? "success" : "brand"}
          />
        </div>
      </Card>

      <div
        className="grid grid-cols-1 gap-3 sm:grid-cols-[3fr_2fr]"
        aria-hidden="true"
      >
        <Card
          padding="compact"
          className="rounded-ds-xl bg-ds-paper-marketing flex items-center border-transparent"
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={status.key}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
              className="flex w-full"
            >
              <Badge
                tone={status.tone}
                shape="pill"
                size="md"
                className="text-ds-body w-full justify-between gap-3 border-transparent"
              >
                {status.label}
                {done ? (
                  <CheckCheck className="size-5" aria-hidden="true" />
                ) : (
                  <LoaderCircle
                    className="size-5 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                )}
              </Badge>
            </motion.span>
          </AnimatePresence>
        </Card>

        <Card
          padding="compact"
          className="rounded-ds-xl bg-ds-paper-marketing flex items-center justify-center border-transparent"
        >
          <MessagesAvatarStack active={running && !done} />
        </Card>
      </div>
    </figure>
  );
}
